import {
  type SavedBoardFilter,
  SavedBoardFilterSchema,
  type UserPreferences,
} from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { PREFERENCES_QUERY_KEY } from './sticky-filters.js';

type PreferencesResponse = { preferences: UserPreferences };

/**
 * Nur gültige Einträge übernehmen: Die API liefert die Präferenzen ungeprüft. Ein Eintrag, der
 * ein später verschärftes Schema verletzt, würde sonst jeden weiteren Schreibvorgang (die ganze
 * Liste geht mit) mit 400 blockieren — so fällt er beim nächsten Speichern still heraus.
 */
function gueltigeEintraege(roh: unknown): SavedBoardFilter[] {
  if (!Array.isArray(roh)) return [];
  return roh.flatMap((e) => {
    const r = SavedBoardFilterSchema.safeParse(e);
    return r.success ? [r.data] : [];
  });
}

function mitBoard(
  prev: PreferencesResponse | undefined,
  board: SavedBoardFilter[] | undefined,
): PreferencesResponse | undefined {
  if (!prev) return prev;
  return {
    preferences: {
      ...prev.preferences,
      saved_filters: { ...prev.preferences.saved_filters, board },
    },
  };
}

/**
 * Gespeicherte Filter des Phase Boards (#141) am Konto: `preferences.saved_filters.board`.
 * Lesen aus derselben Präferenz-Abfrage wie Board und Sticky-Hook (react-query dedupliziert).
 * Schreiben immer als ganze Liste — die API ersetzt sie je Seite (`mergePreferences`).
 *
 * Mehrere Tabs/Geräte: „letzter Schreiber gewinnt" für die Liste — bewusst hingenommen wie bei
 * den gemerkten Filtern; ein Refetch beim Fokuswechsel holt den Stand der anderen Seite.
 *
 * Der Cache wird sofort gesetzt (nur `saved_filters.board`, damit ein zwischenzeitlicher
 * Sticky-Stand nicht mit zurückgedreht wird); scheitert das Speichern, kommt die alte Liste
 * zurück. `bereit` ist erst wahr, wenn die Präferenzen geladen sind — vorher wäre ein Schreiben
 * ein künstliches Präferenz-Objekt, auf das der Sticky-Hook hydrieren würde.
 */
export function useSavedBoardFilters(): {
  liste: SavedBoardFilter[];
  bereit: boolean;
  speichere: (liste: SavedBoardFilter[]) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => api.getMyPreferences(),
  });
  const mutation = useMutation({
    mutationFn: (board: SavedBoardFilter[]) =>
      api.updateMyPreferences({ saved_filters: { board } }),
    onMutate: async (board) => {
      // Ein laufender Refetch (z. B. nach dem Ausblenden einer Phase) überschriebe sonst das
      // sofortige Update — der Eintrag flackerte.
      await queryClient.cancelQueries({ queryKey: PREFERENCES_QUERY_KEY });
      const vorher =
        queryClient.getQueryData<PreferencesResponse>(PREFERENCES_QUERY_KEY)?.preferences
          .saved_filters?.board;
      queryClient.setQueryData<PreferencesResponse | undefined>(PREFERENCES_QUERY_KEY, (prev) =>
        mitBoard(prev, board),
      );
      return { vorher };
    },
    onError: (_err, _board, ctx) => {
      queryClient.setQueryData<PreferencesResponse | undefined>(PREFERENCES_QUERY_KEY, (prev) =>
        mitBoard(prev, ctx?.vorher),
      );
    },
    onSuccess: (res) => queryClient.setQueryData(PREFERENCES_QUERY_KEY, res),
  });
  return {
    liste: gueltigeEintraege(prefsQuery.data?.preferences.saved_filters?.board),
    bereit: prefsQuery.isSuccess,
    speichere: async (liste) => {
      await mutation.mutateAsync(liste);
    },
  };
}
